<script setup lang="ts">
import ButtonEdit from '@/assets/icons/Buttons/ButtonEdit.vue';
// import GenerationInfos from '@/components/Admin/GenerationInfos.vue';
import BaseLoader from '@/components/BaseComponents/BaseLoader.vue';
import RecipeStep from '@/components/Menus/RecipeStep.vue';
import { useFetch } from '@/composables/useFetch.js';
import { type MessageSchema } from '@/i18n';
import { useDishCreationEditionFormStore, useDishesStore, useRecipeStore } from '@/stores/dishesStore';
import { useEnvStore } from '@/stores/envStore.js';
import type { Dish, Ingredient, Recipe } from '@/types/dish.js';
import { adjustUnitsForQuantity, calculateIngredientPrice } from '@/utils/prices';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import { computed, onBeforeUnmount, onMounted, onUnmounted, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
// import html2pdf from 'html2pdf.js'
import AvatarLogo from '@/assets/images/avatar-rounded.webp';
import { useFileDownload } from '@/composables/useFileDownload';
import { Capacitor } from '@capacitor/core';
import jsPDF from 'jspdf';
import { useToast } from 'primevue/usetoast';

// Add these imports
import DishImage from '@/components/Menus/DishImage.vue';
import NutritionalValuesCard from '@/components/Menus/MenuDishViewComponents/NutritionalValuesCard.vue';
import { usePermissions } from '@/composables/usePermissions';

const { t, te } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const { can } = usePermissions();

const router = useRouter();
const route = useRoute();
const toast = useToast();
const { downloadFile } = useFileDownload();
const dishId = route.params.id;
const mustReload = route.query.mustReload === 'true' ? true : false;

const { fetchData } = useFetch();

const recipeStore = useRecipeStore();
const recipe: Ref<Recipe | null> = ref(null);
// const infoTotal = recipeStore.infoTotal;

const dishesStore = useDishesStore();
const dish: Ref<Dish | null> = ref(null);
const isGeneratingPDF = ref(false);

const originalTitle = document.title;

function formatDocumentTitle(): string {
  const date = new Date().toISOString().split('T')[0];
  const formattedDishName = (dish.value?.name || 'recipe').toLowerCase().replace(/\s+/g, '-');
  return `${date}-gstr-${formattedDishName}`;
}

function handleBeforePrint() {
  document.title = formatDocumentTitle();
}

function handleAfterPrint() {
  // document.title = originalTitle
}

function editRecipe() {
  console.log('edit recipe');
  console.log('dish.value ', dish.value);
  if (dish.value && recipe.value) {
    // const adjustedIngredients = listIngredients.value
    const adjustedIngredients = dish.value.ingredients;

    useDishCreationEditionFormStore().creationEditionDish = {
      dish_data: {
        id: dish.value.id,
        name: dish.value.name,
        chill_time_min: dish.value.chill_time_min,
        cook_time_min: dish.value.cook_time_min,
        prep_time_min: dish.value.prep_time_min,
        difficulty: dish.value.difficulty,
        servings: dish.value.servings,
        ingredients: adjustedIngredients,
        steps: recipe.value.steps,
      },
    };
    router.push({ name: 'dish-creation-edition-form', query: { edit: 'true' } });
  }
}

onMounted(async () => {
  document.title = formatDocumentTitle();
  window.addEventListener('beforeprint', handleBeforePrint);
  window.addEventListener('afterprint', handleAfterPrint);

  // reload dish if none or different id or forced reload
  if (!dishesStore.selectedDish || String(dishesStore.selectedDish.id) !== String(dishId) || mustReload) {
    const { data, error } = await fetchData(useEnvStore().apiUrl + `/dishes/${dishId}`);
    if (error) {
      console.error(error);
    } else {
      dish.value = data as Dish;
      dish.value.ingredients = dish.value.ingredients.map((ingredient) => {
        return {
          ...ingredient,
          quantity: ingredient.quantity === 0 ? undefined : ingredient.quantity,
        };
      });
      dishesStore.selectedDish = dish.value;
    }
  } else {
    dishesStore.selectedDish.ingredients = dishesStore.selectedDish.ingredients.map((ingredient) => {
      return {
        ...ingredient,
        quantity: ingredient.quantity === 0 ? undefined : ingredient.quantity,
      };
    });
    dish.value = dishesStore.selectedDish;
  }

  // reload recipe if none or different dish_id or forced reload
  if (!recipeStore.recipe || String(recipeStore.recipe.dish_id) !== String(dishId) || mustReload) {
    const { data, error } = await fetchData(useEnvStore().apiUrl + `/dishes/${dishId}/recipe`);
    if (error) {
      console.error(error);
    } else {
      recipe.value = data as Recipe;
      recipeStore.recipe = recipe.value;
    }
  } else {
    recipe.value = recipeStore.recipe;
  }
});

onUnmounted(() => {
  document.title = originalTitle;
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeprint', handleBeforePrint);
  window.removeEventListener('afterprint', handleAfterPrint);
});

const listIngredients = computed(() => {
  if (!dish.value) return [];

  const servings = dish.value.servings;

  const ingredients = dish.value.ingredients.map((ingredient: Ingredient) => {
    let converted = { unit: ingredient.unit || '', quantity: ingredient.quantity || 0 };
    converted = adjustUnitsForQuantity(converted.quantity, converted.unit, servings);
    return {
      ...ingredient,
      quantity: converted.quantity,
      unit: converted.unit,
      price: calculateIngredientPrice(ingredient.price, servings),
    };
  });

  ingredients.sort((a, b) => a.id - b.id);

  return ingredients;
});

// Add this computed property
const hasImage = computed(() => {
  return (dish.value?.image_url_gastro || dish.value?.image_url_bistro || dish.value?.image_url_user) && dish.value?.selected_image != 'none';
});

// Permission check for editing dishes
const canEditDish = computed(() => can('dishes', 'update'));

// Update the time formatting function
function formatTime(minutes: number): string {
  if (minutes === null || minutes === undefined) return '';
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts: string[] = [];
  if (hrs > 0) {
    parts.push(t('common.hoursShort', { count: hrs }));
    if (mins > 0) {
      parts.push(mins.toString());
    }
  } else if (mins > 0) {
    parts.push(t('common.minutesShort', { count: mins }));
  }
  return parts.join('');
}

async function generatePDF() {
  if (isGeneratingPDF.value) return;

  if (!dish.value || !recipe.value) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Recipe data not available',
      life: 3000,
    });
    return;
  }

  isGeneratingPDF.value = true;

  // For web platform, use browser print
  if (!Capacitor.isNativePlatform()) {
    window.print();
    isGeneratingPDF.value = false;
    return;
  }

  // For native platforms, generate PDF using jsPDF
  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Add fonts - only on web platform to avoid native compatibility issues
    let hasCustomFonts = false;
    if (!Capacitor.isNativePlatform()) {
      try {
        // Dynamically import fonts only for web platform
        const [OpenSansBold, OpenSansRegular, OpenSansLight] = await Promise.all([
          import('@/assets/fonts/open-sans/OpenSans-Bold.ttf'),
          import('@/assets/fonts/open-sans/OpenSans-Regular.ttf'),
          import('@/assets/fonts/open-sans/OpenSans-Light.ttf'),
        ]);

        doc.addFont(OpenSansBold.default, 'OpenSans-Bold', 'bold');
        doc.addFont(OpenSansRegular.default, 'OpenSans-Regular', 'regular');
        doc.addFont(OpenSansLight.default, 'OpenSans-Light', 'light');
        hasCustomFonts = true;
      } catch {
        hasCustomFonts = false;
      }
    } else {
      hasCustomFonts = false;
    }

    // Font helper functions
    const setBoldFont = (size?: number) => {
      try {
        if (hasCustomFonts) {
          doc.setFont('OpenSans-Bold', 'bold');
        } else {
          doc.setFont('helvetica', 'bold');
        }
        if (size) doc.setFontSize(size);
      } catch {
        doc.setFont('helvetica', 'bold');
        if (size) doc.setFontSize(size);
      }
    };

    const setRegularFont = (size?: number) => {
      try {
        if (hasCustomFonts) {
          doc.setFont('OpenSans-Regular', 'regular');
        } else {
          doc.setFont('helvetica', 'normal');
        }
        if (size) doc.setFontSize(size);
      } catch {
        doc.setFont('helvetica', 'normal');
        if (size) doc.setFontSize(size);
      }
    };

    const setLightFont = (size?: number) => {
      try {
        if (hasCustomFonts) {
          doc.setFont('OpenSans-Light', 'light');
        } else {
          doc.setFont('helvetica', 'normal');
        }
        if (size) doc.setFontSize(size);
      } catch {
        doc.setFont('helvetica', 'normal');
        if (size) doc.setFontSize(size);
      }
    };

    // Page setup
    const margin = 15;
    const pageWidth = 210;
    const contentWidth = pageWidth - margin * 2;
    const lineHeight = 6;
    let y = margin;

    // Load avatar logo for footer (preserve aspect ratio, downscale for small footer usage)
    let logoAspectRatio = 1;
    const logoData: string | null = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const w = img.naturalWidth || img.width;
          const h = img.naturalHeight || img.height;
          logoAspectRatio = w / h;
          // Downscale logo to max 64px height (it's only 8mm in the footer)
          const maxLogoH = 64;
          const scale = Math.min(1, maxLogoH / h);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(w * scale);
          canvas.height = Math.round(h * scale);
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/png'));
          } else {
            resolve(null);
          }
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = AvatarLogo;
    });

    // Title
    setBoldFont(18);
    doc.setTextColor(33, 45, 64);
    const titleLines = doc.splitTextToSize(dish.value.name, contentWidth);
    doc.text(titleLines, margin, y, { align: 'left' });
    y += titleLines.length * lineHeight + lineHeight;
    doc.setTextColor(0, 0, 0);

    // Fetch dish image via API (same endpoint as DishImage component)
    let imageData: string | null = null;
    if (hasImage.value && dish.value && dish.value.selected_image && dish.value.selected_image !== 'none') {
      try {
        const { data: imgData, error: imgError } = await fetchData(
          `${useEnvStore().apiUrl}/dishes/${dish.value.id}/images/${dish.value.selected_image}`,
        );
        if (!imgError && imgData && imgData.image) {
          imageData = imgData.image; // Already a base64 data URL
        } else {
          console.warn('Failed to fetch image from API:', imgError);
        }
      } catch (error) {
        console.warn('Error fetching image for PDF:', error);
      }

      // Fallback: try extracting from the already-loaded DOM element
      if (!imageData) {
        try {
          const imgElement = document.querySelector(`img[alt="${dish.value.name}"]`) as HTMLImageElement;
          if (imgElement && imgElement.complete && imgElement.naturalWidth > 0) {
            const origW = imgElement.naturalWidth || imgElement.width;
            const origH = imgElement.naturalHeight || imgElement.height;
            const maxPx = 800;
            const scale = Math.min(1, maxPx / Math.max(origW, origH));
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(origW * scale);
            canvas.height = Math.round(origH * scale);
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(imgElement, 0, 0, canvas.width, canvas.height);
              imageData = canvas.toDataURL('image/jpeg', 0.7);
            }
          }
        } catch (domError) {
          console.warn('Failed to extract image from DOM:', domError);
        }
      }
    }

    // Recipe Information + optional image layout
    const sectionTitleSize = 15;
    const labelFontSize = 10;
    const blockStartY = y;
    let imageHeight = 0;
    let imageWidth = 0;
    const imageGap = 8;

    if (imageData) {
      try {
        const imageProps = doc.getImageProperties(imageData);
        const maxImageWidth = 100;
        const maxImageHeight = 75;
        imageWidth = maxImageWidth;
        imageHeight = (imageProps.height / imageProps.width) * imageWidth;
        if (imageHeight > maxImageHeight) {
          imageHeight = maxImageHeight;
          imageWidth = (imageProps.width / imageProps.height) * imageHeight;
        }

        // Pre-process the image on a canvas to apply rounded corners + downscale for PDF
        const roundedImageData: string | null = await new Promise((resolve) => {
          const tempImg = new Image();
          tempImg.onload = () => {
            try {
              const origW = tempImg.naturalWidth || tempImg.width;
              const origH = tempImg.naturalHeight || tempImg.height;
              // Cap canvas resolution: 100mm at ~3x ≈ 800px max width is plenty for PDF
              const maxCanvasPx = 800;
              const scale = Math.min(1, maxCanvasPx / Math.max(origW, origH));
              const canvas = document.createElement('canvas');
              canvas.width = Math.round(origW * scale);
              canvas.height = Math.round(origH * scale);
              const ctx = canvas.getContext('2d');
              if (ctx) {
                // Fill entire canvas white FIRST (JPEG has no transparency — unfilled areas become black)
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                // Now clip to rounded rectangle and draw the image inside
                const cornerRadius = Math.min(canvas.width, canvas.height) * 0.06;
                ctx.beginPath();
                ctx.moveTo(cornerRadius, 0);
                ctx.lineTo(canvas.width - cornerRadius, 0);
                ctx.quadraticCurveTo(canvas.width, 0, canvas.width, cornerRadius);
                ctx.lineTo(canvas.width, canvas.height - cornerRadius);
                ctx.quadraticCurveTo(canvas.width, canvas.height, canvas.width - cornerRadius, canvas.height);
                ctx.lineTo(cornerRadius, canvas.height);
                ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - cornerRadius);
                ctx.lineTo(0, cornerRadius);
                ctx.quadraticCurveTo(0, 0, cornerRadius, 0);
                ctx.closePath();
                ctx.clip();
                ctx.drawImage(tempImg, 0, 0, canvas.width, canvas.height);
                // Use JPEG at 0.7 quality instead of PNG to drastically reduce size
                resolve(canvas.toDataURL('image/jpeg', 0.7));
              } else {
                resolve(null);
              }
            } catch {
              resolve(null);
            }
          };
          tempImg.onerror = () => resolve(null);
          tempImg.src = imageData!;
        });

        const finalImageData = roundedImageData || imageData;
        doc.addImage(finalImageData, 'JPEG', margin, y, imageWidth, imageHeight);
      } catch (error) {
        console.warn('Error adding image to PDF:', error);
        imageWidth = 0;
        imageHeight = 0;
      }
    }

    // Recipe Information
    const infoX = imageWidth > 0 ? margin + imageWidth + imageGap : margin;
    // const infoWidth = imageWidth > 0 ? contentWidth - imageWidth - imageGap : contentWidth;
    let infoY = blockStartY;

    setBoldFont(sectionTitleSize);
    doc.setTextColor(33, 45, 64);
    doc.text(t('profileFavorites.creationForm.informations'), infoX, infoY);
    doc.setTextColor(0, 0, 0);
    infoY += lineHeight;

    setRegularFont(labelFontSize);
    const infoValueX = infoX + 50; // right edge for value alignment
    const drawInfoLine = (label: string, value: string) => {
      if (!value) return;
      doc.text(`${label} :`, infoX, infoY);
      doc.text(value, infoValueX, infoY, { align: 'left' });
      infoY += lineHeight;
    };

    if (dish.value.prep_time_min) {
      drawInfoLine(t('profileFavorites.creationForm.prepTime'), formatTime(dish.value.prep_time_min));
    }
    if (dish.value.chill_time_min) {
      drawInfoLine(t('profileFavorites.creationForm.chillTime'), formatTime(dish.value.chill_time_min));
    }
    if (dish.value.cook_time_min) {
      drawInfoLine(t('profileFavorites.creationForm.cookTime'), formatTime(dish.value.cook_time_min));
    }
    drawInfoLine(t('profileFavorites.creationForm.numberOfPeople'), dish.value.servings?.toString() || '');

    y = Math.max(blockStartY + imageHeight, infoY) + lineHeight * 2;

    // Nutritional Values (if available)
    const hasNutritionalData = dish.value.calories_kcal || dish.value.protein_g || dish.value.carbohydrates_g || dish.value.fat_g;
    if (hasNutritionalData) {
      setBoldFont(sectionTitleSize);
      doc.setTextColor(33, 45, 64);
      doc.text(t('dish.nutritional.nutritionalValues'), margin, y);
      doc.setTextColor(0, 0, 0);
      y += lineHeight;

      // "Pour 1 portion" subtitle
      setBoldFont(labelFontSize);
      doc.text(t('dish.nutritional.forOneServing'), margin, y);
      y += lineHeight;

      // Table layout for nutritional rows
      const nutritionTableWidth = 75; // mm wide
      const valueColumnX = margin + nutritionTableWidth; // right-align values here

      const nutritionRows: { label: string; value: string }[] = [];
      if (dish.value.calories_kcal) {
        nutritionRows.push({ label: t('dish.nutritional.calories'), value: `${Math.round(dish.value.calories_kcal)} kcal` });
      }
      if (dish.value.protein_g) {
        nutritionRows.push({ label: t('dish.nutritional.protein'), value: `${(Math.round(dish.value.protein_g * 100) / 100).toFixed(2)} g` });
      }
      if (dish.value.fat_g) {
        nutritionRows.push({ label: t('dish.nutritional.fat'), value: `${(Math.round(dish.value.fat_g * 100) / 100).toFixed(2)} g` });
      }
      if (dish.value.saturated_fat_g) {
        nutritionRows.push({
          label: t('dish.nutritional.saturatedFat'),
          value: `${(Math.round(dish.value.saturated_fat_g * 100) / 100).toFixed(2)} g`,
        });
      }
      if (dish.value.carbohydrates_g) {
        nutritionRows.push({
          label: t('dish.nutritional.carbohydrates'),
          value: `${(Math.round(dish.value.carbohydrates_g * 100) / 100).toFixed(2)} g`,
        });
      }
      if (dish.value.sugars_g) {
        nutritionRows.push({ label: t('dish.nutritional.sugars'), value: `${(Math.round(dish.value.sugars_g * 100) / 100).toFixed(2)} g` });
      }
      if (dish.value.dietary_fiber_g) {
        nutritionRows.push({
          label: t('dish.nutritional.dietaryFiber'),
          value: `${(Math.round(dish.value.dietary_fiber_g * 100) / 100).toFixed(2)} g`,
        });
      }
      if (dish.value.salt_sodium_g) {
        nutritionRows.push({ label: t('dish.nutritional.salt'), value: `${(Math.round(dish.value.salt_sodium_g * 100) / 100).toFixed(2)} g` });
      }

      setRegularFont(labelFontSize);
      nutritionRows.forEach((row) => {
        // Label on left
        doc.text(row.label, margin, y);
        // Value right-aligned
        doc.text(row.value, valueColumnX, y, { align: 'right' });
        // Separator line below
        y += 1.5;
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(margin, y, margin + nutritionTableWidth, y);
        y += lineHeight - 1;
      });

      y += lineHeight * 0.5;

      // Allergens section
      if (dish.value.allergens && dish.value.allergens.trim() !== '') {
        setBoldFont(labelFontSize);
        doc.text(t('dish.allergens.title'), margin, y);
        y += lineHeight;
        setRegularFont(labelFontSize);
        const allergenLines = doc.splitTextToSize(dish.value.allergens, contentWidth);
        allergenLines.forEach((line: string) => {
          doc.text(line, margin, y);
          y += lineHeight;
        });
      }

      y += lineHeight; // Extra space after nutritional values
    }

    // Ingredients — start on a new page when there's an image on page 1
    if (imageData) {
      doc.addPage();
      y = margin;
    }
    setBoldFont(sectionTitleSize);
    doc.setTextColor(33, 45, 64);
    doc.text(t('profileFavorites.creationForm.ingredients'), margin, y);
    doc.setTextColor(0, 0, 0);
    y += lineHeight * 1.5;

    // Ingredients table columns
    const colQtyX = margin;
    const colUnitX = margin + 25;
    const colNameX = margin + 50;
    const tableRight = margin + contentWidth;

    // Draw table header
    const drawIngredientHeader = () => {
      setBoldFont(labelFontSize);
      doc.text(t('profileFavorites.creationForm.quantityShort'), colQtyX, y);
      doc.text(t('profileFavorites.creationForm.unit'), colUnitX, y);
      doc.text(t('profileFavorites.creationForm.name'), colNameX, y);
      y += 1.5;
      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.3);
      doc.line(margin, y, tableRight, y);
      y += lineHeight;
    };

    drawIngredientHeader();

    setRegularFont(labelFontSize);
    listIngredients.value.forEach((ingredient) => {
      // Check if we need a new page before adding ingredient
      if (y > 270) {
        doc.addPage();
        y = margin;
        setBoldFont(sectionTitleSize);
        doc.setTextColor(33, 45, 64);
        doc.text(t('profileFavorites.creationForm.ingredients'), margin, y);
        doc.setTextColor(0, 0, 0);
        y += lineHeight * 1.5;
        drawIngredientHeader();
        setRegularFont(labelFontSize);
      }

      const quantity = ingredient.quantity > 0 ? String(ingredient.quantity) : '';
      const unit =
        ingredient.unit && te(`ingredients.unit.symbol.${ingredient.unit}`) ? t(`ingredients.unit.symbol.${ingredient.unit}`) : ingredient.unit || '';

      doc.text(quantity, colQtyX, y);
      doc.text(unit, colUnitX, y);
      doc.text(ingredient.name, colNameX, y);

      // Separator line
      y += 2.5;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.2);
      doc.line(margin, y, tableRight, y);
      y += lineHeight;
    });

    y += lineHeight;

    // Recipe Steps
    if (recipe.value.steps && recipe.value.steps.length > 0) {
      setBoldFont(sectionTitleSize);
      doc.setTextColor(33, 45, 64);
      doc.text(t('profileFavorites.creationForm.instructions'), margin, y);
      doc.setTextColor(0, 0, 0);
      y += lineHeight;

      recipe.value.steps.forEach((step) => {
        // Check if we need a new page for the step
        if (y > 260) {
          doc.addPage();
          y = margin;
        }

        // Step name
        setBoldFont(12);
        doc.text(step.name, margin, y);
        y += lineHeight;

        // Step instructions
        setRegularFont(labelFontSize);
        const checkboxSize = 3.5; // mm
        const checkboxGap = 2.5; // gap between checkbox and text
        const textIndent = margin + checkboxSize + checkboxGap;

        step.instructions.forEach((instruction, index) => {
          if (instruction.instruction.trim()) {
            const text = `${index + 1}. ${instruction.instruction}`;
            const lines = doc.splitTextToSize(text, pageWidth - margin * 2 - checkboxSize - checkboxGap);

            lines.forEach((line: string, lineIdx: number) => {
              if (y > 270) {
                doc.addPage();
                y = margin;
              }
              // Draw checkbox only on the first line of each instruction
              if (lineIdx === 0) {
                doc.setDrawColor(150, 150, 150);
                doc.setLineWidth(0.3);
                doc.rect(margin, y - checkboxSize + 0.5, checkboxSize, checkboxSize);
              }
              doc.text(line, textIndent, y);
              y += lineHeight;
            });
          }
        });

        y += lineHeight; // Space between steps
      });
    }

    // Add footer to all pages
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(doc, margin, i, totalPages, setBoldFont, setRegularFont, setLightFont, logoData, logoAspectRatio);
    }

    // Generate filename
    const date = new Date().toISOString().split('T')[0];
    const formattedDishName = dish.value.name.toLowerCase().replace(/\s+/g, '-');
    const fileName = `${date}-gstr-${formattedDishName}.pdf`;

    // Save the PDF
    const pdfBlob = doc.output('blob');
    await downloadFile({
      fileName,
      data: pdfBlob,
      mimeType: 'application/pdf',
      showShareDialog: true,
    });

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Recipe PDF generated successfully',
      life: 3000,
    });
  } catch (error) {
    isGeneratingPDF.value = false;
    console.error('Error generating PDF:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to generate PDF. Please try again.',
      life: 5000,
    });
    return;
  }

  isGeneratingPDF.value = false;

  // Helper function to add footer
  function addFooter(
    doc: jsPDF,
    margin: number,
    currentPage: number,
    totalPages: number,
    setBoldFont: (size?: number) => void,
    setRegularFont: (size?: number) => void,
    setLightFont: (size?: number) => void,
    logoData: string | null,
    logoAspectRatio: number,
  ) {
    const pageWidth = 210;
    const pageHeight = 297;
    const footerY = pageHeight - margin / 2;
    const footerCenterY = footerY - 7.5;

    // Draw logo + "Powered by GastronomIA" text
    let textStartX = margin;
    const logoHeight = 8; // 8mm tall
    const logoWidth = logoHeight * logoAspectRatio; // preserve aspect ratio

    if (logoData) {
      try {
        doc.addImage(logoData, 'PNG', margin, footerCenterY - logoHeight + 2, logoWidth, logoHeight);
        textStartX = margin + logoWidth + 1.5;
      } catch {
        // If logo fails, just skip it
      }
    }

    setLightFont(7);
    const poweredByText = 'Powered by ';
    const poweredByWidth = doc.getTextWidth(poweredByText);

    doc.setTextColor(100, 100, 100);
    doc.text(poweredByText, textStartX, footerCenterY);
    setBoldFont(7);
    doc.text('GastronomIA', textStartX + poweredByWidth, footerCenterY);

    // Add page numbers (only if more than one page)
    if (totalPages > 1) {
      setRegularFont(7);
      const pageText = `${currentPage}/${totalPages}`;
      doc.text(pageText, pageWidth - margin, footerCenterY, { align: 'right' });
    }

    // Reset text color
    doc.setTextColor(0, 0, 0);
  }
}
</script>

<template>
  <table class="print-container w-full">
    <tbody>
      <tr>
        <td>
          <main>
            <div class="my hidden print:block">
              <div class="mx-auto flex max-w-screen-xl items-center justify-between py-1">
                <div class="flex items-center gap-2">
                  <img :src="AvatarLogo" alt="GastronomIA Logo" class="h-8 w-8 object-contain" />
                  <p class="text-xs">
                    <span class="text-gray-600">Powered by</span>
                    <span class="ml-1 font-bold text-primary-700">GastronomIA</span>
                  </p>
                </div>
              </div>
            </div>
            <div v-if="recipe && dish" class="flex flex-col gap-6">
              <div class="flex flex-row justify-end gap-2 align-middle print:hidden">
                <!-- Add onboarding classes for Shepherd targeting -->
                <Button
                  rounded
                  link
                  class="onboarding-export-pdf-button"
                  :pt="{ root: { class: '!p-1.5 !w-10 !h-10' } }"
                  :pt-options="{ mergeProps: true }"
                  :loading="isGeneratingPDF"
                  :disabled="isGeneratingPDF"
                  @click="generatePDF"
                >
                  <!-- PrimeVue default icon -->
                  <i v-if="!isGeneratingPDF" class="pi pi-file-pdf text-xl"></i>
                </Button>

                <Button
                  v-if="canEditDish"
                  rounded
                  link
                  class="onboarding-edit-recipe-button"
                  :pt="{ root: { class: '!p-1.5 !w-10 !h-10' } }"
                  :pt-options="{ mergeProps: true }"
                  @click="editRecipe"
                >
                  <!-- Custom icon with adjusted size -->
                  <template #icon>
                    <ButtonEdit class="text-xl" />
                    <!-- Ensure ButtonEdit scales properly -->
                  </template>
                </Button>
              </div>
              <div class="my-2 flex justify-between">
                <h1>{{ dish.name }}</h1>
              </div>
              <div class="flex flex-row flex-wrap items-start gap-6" :class="{ 'justify-between': !hasImage, 'justify-start': hasImage }">
                <div v-if="hasImage" class="mr-1 hidden w-72 items-start justify-start text-center print:flex">
                  <DishImage :dish-id="dish.id" :type="dish.selected_image" :alt="dish.name" class="m-auto w-72" size="full" />
                </div>
                <div class="my-1">
                  <h2>{{ t('profileFavorites.creationForm.informations') }}</h2>
                  <p class="grid grid-cols-4">
                    <span class="col-span-3"> {{ t('profileFavorites.creationForm.prepTime') }} : </span>
                    <span> {{ formatTime(dish.prep_time_min) }} </span>
                  </p>
                  <p class="grid grid-cols-4">
                    <span class="col-span-3"> {{ t('profileFavorites.creationForm.chillTime') }} : </span>
                    <span>{{ formatTime(dish.chill_time_min) }} </span>
                  </p>
                  <p class="grid grid-cols-4">
                    <span class="col-span-3">{{ t('profileFavorites.creationForm.cookTime') }} : </span>
                    <span>{{ formatTime(dish.cook_time_min) }}</span>
                  </p>
                  <p class="grid grid-cols-4">
                    <span class="col-span-3">{{ t('profileFavorites.creationForm.numberOfPeople') }} : </span>
                    <span>{{ dish.servings }}</span>
                  </p>
                </div>
                <div class="my-1 hidden print:flex print:flex-col">
                  <h2 class="mb-1">{{ t('dish.nutritional.nutritionalValues') }}</h2>
                  <NutritionalValuesCard :dish="dish" small />
                </div>
              </div>
              <div class="my-2" :class="[hasImage ? 'print:break-before-page' : '']">
                <h2>{{ t('profileFavorites.creationForm.ingredients') }}</h2>
                <DataTable :value="listIngredients" size="small">
                  <Column field="quantity" :header="t('profileFavorites.creationForm.quantityShort')">
                    <template #body="slotProps">
                      {{ slotProps.data.quantity > 0 ? slotProps.data.quantity : '' }}
                    </template>
                  </Column>
                  <Column field="unit" :header="t('profileFavorites.creationForm.unit')">
                    <template #body="slotProps">
                      {{ te(`ingredients.unit.symbol.${slotProps.data.unit}`) ? t(`ingredients.unit.symbol.${slotProps.data.unit}`) : '' }}
                    </template>
                  </Column>
                  <!-- <Column field="unit" :header="t('profileFavorites.creationForm.unit')" /> -->
                  <Column field="name" :header="t('profileFavorites.creationForm.name')" />
                  <Column field="price" :header="t('profileFavorites.creationForm.price')" class="print:hidden" />
                </DataTable>
              </div>
              <div v-if="recipe" class="my-4">
                <h2>{{ t('profileFavorites.creationForm.instructions') }}</h2>
                <div class="flex flex-col gap-6">
                  <RecipeStep v-for="step in recipe.steps" :key="step.name" :step-name="step.name" :instructions="step.instructions" />
                </div>
              </div>

              <!-- <GenerationInfos v-if="infoTotal" :info-total="infoTotal" /> -->
            </div>
            <div v-else class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <BaseLoader />
            </div>
          </main>
        </td>
      </tr>
    </tbody>
    <tfoot>
      <tr>
        <td>
          <div class="footer-space">&nbsp;</div>
        </td>
      </tr>
    </tfoot>
  </table>

  <footer class="hidden print:block">
    <div class="mx-auto flex max-w-screen-xl items-center justify-between py-1">
      <div class="flex items-center gap-2">
        <img :src="AvatarLogo" alt="GastronomIA Logo" class="h-8 w-8 object-contain" />
        <p class="text-xs">
          <span class="text-gray-600">Powered by</span>
          <span class="ml-1 font-bold text-primary-700">GastronomIA</span>
        </p>
      </div>
    </div>
  </footer>
</template>

<style scoped>
@page {
  margin: 10mm;
}

.print-container {
  width: 100%;
}

.footer-space {
  height: 50px;
}

.fixed-footer {
  position: fixed;
  bottom: 0;
  width: 100%;
  background-color: white;
}

@media print {
  .print-container {
    width: 100%;
  }

  .fixed-footer {
    position: fixed;
    bottom: 0;
  }

  .print\:break-before-page {
    break-before: page;
  }

  .print\:hidden {
    display: none;
  }
}
</style>
